import {
  Button,
  CustomFilter,
  EllipsisSpan,
  Form,
  Input,
  Modal,
  Paging,
  Select,
  Table,
  Tag,
  Wrapper,
  message,
  useBreadcrumbRoutes,
  usePaging,
  useUpdate,
} from '@maxtropy/components';
import { Space, Upload, Popconfirm } from 'antd';
import { Link } from 'react-router-dom';
import React, { useState } from 'react';
import styles from './index.module.scss';
import { useRequest } from 'ahooks';
import { UploadChangeParam } from 'antd/es/upload';
import { UploadFile } from 'antd/es/upload/interface';
import {
  apiV2SimCardBatchDeletePost,
  apiV2SimCardPagePost,
  apiV2SimCardDeviceListPost,
  apiV2SimCardGatewayListPost,
  V2SimCardPagePostRequest,
  V2SimCardPagePostResponse,
} from '@maxtropy/device-customer-apis-v2';

// SIM卡状态颜色映射
const SimStatusColorMap: Record<string, string> = {
  可测试: 'warning',
  可激活: 'info',
  已激活: 'success',
  已停用: 'error',
  已失效: 'invalid',
  已更换: 'success',
  已清除: 'invalid',
  库存: 'info',
  开始: 'success',
  预清除: 'warning',
};

// 表格列配置
const columns = [
  {
    title: 'SIM卡ICCID',
    dataIndex: 'iccid',
    ellipsis: true,
    render: (v: string) => <EllipsisSpan value={v ?? '-'} />,
  },
  {
    title: 'SIM卡状态',
    dataIndex: 'simStatusDisplay',
    ellipsis: true,
    render: (v: string) =>
      v ? (
        <Tag border="solid" color={SimStatusColorMap[v] || 'default'}>
          {v}
        </Tag>
      ) : (
        <EllipsisSpan value="-" />
      ),
  },
  {
    title: '关联网关/设备',
    dataIndex: 'deviceName',
    ellipsis: true,
    render: (_: unknown, record: SimCardDataItem) => {
      // 优先展示设备
      if (record.deviceCode) {
        return (
          <Link to={`/device/manage/device/${record.deviceId}/detail`} target="_blank">
            <EllipsisSpan value={record.deviceName ?? '-'} />
          </Link>
        );
      }
      // 其次展示网关
      if (record.gatewayCode) {
        return (
          <Link to={`/device/config/gateway?serialNumber=${encodeURIComponent(record.gatewayCode)}`} target="_blank">
            <EllipsisSpan value={record.gatewayName ?? '-'} />
          </Link>
        );
      }
      return <EllipsisSpan value="-" />;
    },
  },
];

export interface FilterParams extends Omit<V2SimCardPagePostRequest, 'timestamp' | 'page' | 'size'> {
  deviceId?: number;
  gatewayId?: number;
}

// 表格数据类型
export type SimCardDataItem = NonNullable<V2SimCardPagePostResponse['list']>[number];

const SimCardManage: React.FC = () => {
  const breadcrumbRoutes = useBreadcrumbRoutes();
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
  const [searchParams, setSearchParams] = useState<FilterParams>({});
  const pagingInfo = usePaging(50);
  const { pageOffset, pageSize, setTotalCount, setPageOffset } = pagingInfo;
  const [updateState, updateFn] = useUpdate();

  // 获取表格数据
  const { data: tableData, loading } = useRequest(
    () =>
      apiV2SimCardPagePost({
        ...searchParams,
        page: pageOffset,
        size: pageSize,
      }).then(res => {
        setTotalCount(res.total!);
        return res.list || [];
      }),
    {
      refreshDeps: [pageOffset, pageSize, searchParams, updateState],
    }
  );

  // 获取关联设备列表
  const { data: deviceList } = useRequest(() => apiV2SimCardDeviceListPost({}).then(res => res.list || []));
  const deviceOptions = deviceList?.map(item => ({
    label: item.deviceName ?? '',
    value: item.deviceId,
  }));

  // 获取关联网关列表
  const { data: gatewayList } = useRequest(() => apiV2SimCardGatewayListPost({}).then(res => res.list || []));
  const gatewayOptions = gatewayList?.map(item => ({
    label: item.gatewayName ?? '',
    value: item.gatewayId,
  }));

  // 导入
  const handleImport = (val: UploadChangeParam<UploadFile>) => {
    if (val.file.status === 'error') {
      Modal.error({
        title: '导入失败！',
      });
    }
    if (val.file.status === 'done') {
      const response = val.file.response;
      if (response && response.code === 0) {
        const successCount = response.data?.successCount ?? 0;
        const failCount = response.data?.failCount ?? 0;
        if (successCount === 0) {
          Modal.error({
            title: '导入失败！',
            content: `导入成功 ${successCount} 条，导入失败 ${failCount} 条`,
          });
        } else {
          Modal.success({
            title: '导入成功！',
            content: `导入成功 ${successCount} 条，导入失败 ${failCount} 条`,
          });
        }
        updateFn();
      } else {
        Modal.error({
          title: response?.msg || '导入失败!',
        });
      }
    }
  };

  // 批量删除
  const handleBatchDelete = () => {
    if (selectedRowKeys.length === 0) {
      message.warning('请先选择要删除的数据');
      return;
    }
    Modal.confirm({
      title: '确认删除',
      content: `确定要删除选中的 ${selectedRowKeys.length} 条数据吗？删除后不可恢复。`,
      onOk: async () => {
        try {
          await apiV2SimCardBatchDeletePost({ ids: selectedRowKeys.map(id => Number(id)) });
          message.success('删除成功');
          setSelectedRowKeys([]);
          updateFn();
        } catch (error) {
          message.error('删除失败');
        }
      },
    });
  };

  // 删除单条数据
  const handleDelete = async (record: SimCardDataItem) => {
    try {
      await apiV2SimCardBatchDeletePost({ ids: [Number(record.id)] });
      message.success('删除成功');
      updateFn();
    } catch (error) {
      message.error('删除失败');
    }
  };

  const buildColumns = [
    ...columns,
    {
      title: '操作',
      key: 'action',
      width: 150,
      fixed: 'right' as const,
      render: (_: unknown, record: SimCardDataItem) => (
        <Popconfirm title="你确定要删除吗？" okText="确认" cancelText="取消" onConfirm={() => handleDelete(record)}>
          <Button type="link">删除</Button>
        </Popconfirm>
      ),
    },
  ];

  // 筛选条件
  const onFinish = (values: FilterParams) => {
    setPageOffset(1);
    setSearchParams(values);
  };

  const onReset = () => {
    setPageOffset(1);
    setSearchParams({});
    form.resetFields();
  };

  const filters = (
    <CustomFilter<FilterParams> form={form} onFinish={onFinish} onReset={onReset}>
      <Form.Item name="iccid" label="SIM卡ICCID">
        <Input placeholder="请输入SIM卡ICCID" allowClear />
      </Form.Item>
      <Form.Item name="gatewayId" label="关联网关">
        <Select showSearch placeholder="请选择关联网关" options={gatewayOptions} optionFilterProp="label" allowClear />
      </Form.Item>
      <Form.Item name="deviceId" label="关联设备">
        <Select showSearch placeholder="请选择关联设备" options={deviceOptions} optionFilterProp="label" allowClear />
      </Form.Item>
    </CustomFilter>
  );

  // 行选择配置
  const rowSelection = {
    selectedRowKeys,
    onChange: (newSelectedRowKeys: React.Key[]) => {
      setSelectedRowKeys(newSelectedRowKeys as string[]);
    },
    preserveSelectedRowKeys: true,
  };

  return (
    <Wrapper routes={breadcrumbRoutes?.routes} filters={filters} className={styles.wrapper}>
      <div className={styles.headerActions}>
        <Space size="small">
          <Upload
            action="/api/v2/sim-card/import"
            accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            showUploadList={false}
            onChange={handleImport}
          >
            <Button type="primary">导入</Button>
          </Upload>
          <Button
            type="link"
            onClick={() => {
              window.open('/api/v2/sim-card/template', '_blank');
            }}
          >
            下载已导入配置
          </Button>
          <Button onClick={handleBatchDelete} disabled={selectedRowKeys.length === 0}>
            批量删除
          </Button>
        </Space>
      </div>
      <Table
        rowKey="id"
        sticky
        loading={loading}
        columns={buildColumns}
        dataSource={tableData}
        rowSelection={rowSelection}
        scroll={{ x: 1000 }}
      />
      <Paging pagingInfo={pagingInfo} />
    </Wrapper>
  );
};

export default SimCardManage;
